import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import MagicButton from 'components/MagicButton/MagicButton';
import TextInputField from 'components/TextInputField';
import { AssetUrls } from 'config/assets';
import { sanitizeEmail } from 'utils/inputSanitization';
import { showError, showSuccess } from 'utils/toastNotifications';
import './PasswordResetModal.css';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (email: string) => Promise<void>;
  isLoading?: boolean;
}

const PasswordResetModal: React.FC<Props> = ({ isOpen, onClose, onSubmit, isLoading = false }) => {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setEmail('');
      setEmailError(false);
      setSubmitted(false);
    }
  }, [isOpen]);

  // Auto-close after successful submission
  useEffect(() => {
    if (submitted) {
      const timer = setTimeout(() => {
        onClose();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [submitted, onClose]);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[\w+.-]+@([\w-]+\.)+[\w-]{2,4}$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async () => {
    const sanitizedEmail = sanitizeEmail(email);
    
    if (!validateEmail(sanitizedEmail)) {
      setEmailError(true);
      showError('Please enter a valid email address');
      return;
    }

    try {
      await onSubmit(sanitizedEmail);
      setSubmitted(true);
      showSuccess('Check your email for password reset instructions');
    } catch (error: any) {
      if (error?.data?.status === 429) {
        const message = error.data?.message || 'Too many password reset requests. Please try again later.';
        showError(message);
      } else {
        // Always show generic success message for security
        setSubmitted(true);
        showSuccess('Check your email for password reset instructions');
      }
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="password-reset-modal-overlay" onClick={onClose}>
      <div className="password-reset-modal" onClick={(e) => e.stopPropagation()}>
        <div className="password-reset-modal-header">
          <h2 className="password-reset-modal-title">Reset Password</h2>
          <button className="password-reset-close-button" onClick={onClose}>
            <img
              src={AssetUrls.icons.dropdowns.close}
              alt="Close"
              style={{ width: 16, height: 16 }}
            />
          </button>
        </div>

        {!submitted ? (
          <>
            <div className="password-reset-modal-body">
              <p className="password-reset-description">
                Enter your email address and we'll send you a link to reset your password.
              </p>
              <TextInputField
                label="Email Address"
                value={email}
                error={emailError}
                onChange={(value) => {
                  setEmail(value);
                  if (emailError) setEmailError(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSubmit();
                }}
              />
            </div>

            <div className="password-reset-modal-footer">
              <MagicButton
                label="Send Reset Link"
                color="purple"
                onPress={handleSubmit}
                isLoading={isLoading}
                isDisabled={!email || isLoading}
              />
            </div>
          </>
        ) : (
          <div className="password-reset-modal-body">
            <div className="password-reset-success">
              <div className="password-reset-success-icon">✓</div>
              <h3>Check Your Email</h3>
              <p>
                If an account exists with that email address, we've sent password reset instructions to <strong>{email}</strong>.
              </p>
              <p className="password-reset-note">
                The link will expire in 1 hour for security reasons.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default PasswordResetModal;